import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Coins, Plus, TrendingUp, Calendar, AlertCircle, History, ChevronDown, Filter, XCircle, Download } from 'lucide-react';

interface CreditManagementProps {
  userId: string;
}

interface CreditData {
  credits_regular: number;
  credits_topup: number;
  credits_allocated_monthly: number;
  billing_cycle_start_date: string;
  next_billing_date: string;
  credits_last_refilled_at: string;
  credits_negative_limit: number;
}

interface CreditAllocation {
  id: string;
  allocation_amount: number;
  allocation_type: string;
  credit_type: string;
  billing_period_start: string | null;
  billing_period_end: string | null;
  notes: string | null;
  created_at: string;
}

interface TopupPurchase {
  id: string;
  credits_amount: number;
  package_type: string;
  price_paid: number;
  payment_status: string;
  created_at: string;
  completed_at: string | null;
}

interface CreditLogEntry {
  id: string;
  action_type: string;
  credits_used: number;
  ai_provider: string | null;
  success: boolean;
  created_at: string;
  metadata: any;
}

export function CreditManagement({ userId }: CreditManagementProps) {
  const [loading, setLoading] = useState(true);
  const [creditData, setCreditData] = useState<CreditData | null>(null);
  const [allocations, setAllocations] = useState<CreditAllocation[]>([]);
  const [purchases, setPurchases] = useState<TopupPurchase[]>([]);
  const [creditLogs, setCreditLogs] = useState<CreditLogEntry[]>([]);
  const [showAddTopup, setShowAddTopup] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupNote, setTopupNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [timeframeFilter, setTimeframeFilter] = useState('month');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    loadCreditData();

    // Load saved filters from localStorage
    const savedFilters = localStorage.getItem(`credit-filters-${userId}`);
    if (savedFilters) {
      try {
        const { timeframe, type } = JSON.parse(savedFilters);
        if (timeframe) setTimeframeFilter(timeframe);
        if (type) setTypeFilter(type);
      } catch (e) {
        console.error('Failed to load saved filters:', e);
      }
    }
  }, [userId]);

  // Save filters to localStorage
  useEffect(() => {
    localStorage.setItem(`credit-filters-${userId}`, JSON.stringify({
      timeframe: timeframeFilter,
      type: typeFilter
    }));
  }, [timeframeFilter, typeFilter, userId]);

  const loadCreditData = async () => {
    setLoading(true);
    try {
      const { data: user, error: userError } = await supabase
        .from('users')
        .select('credits_regular, credits_topup, credits_allocated_monthly, billing_cycle_start_date, next_billing_date, credits_last_refilled_at, credits_negative_limit')
        .eq('id', userId)
        .maybeSingle();

      if (userError) throw userError;
      setCreditData(user);

      const { data: allocs, error: allocsError } = await supabase
        .from('credit_allocations')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(10);

      if (!allocsError) setAllocations(allocs || []);

      const { data: purch, error: purchError } = await supabase
        .from('topup_purchases')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(10);

      if (!purchError) setPurchases(purch || []);

      const { data: logs, error: logsError } = await supabase
        .from('credit_log')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (!logsError) setCreditLogs(logs || []);
    } catch (error) {
      console.error('Error loading credit data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTopup = async () => {
    const amount = parseInt(topupAmount);
    if (!amount || isNaN(amount)) {
      setMessage('Voer een geldig aantal credits in');
      return;
    }

    setSaving(true);
    setMessage('');

    try {
      const newTopup = (creditData?.credits_topup || 0) + amount;

      const { error: updateError } = await supabase
        .from('users')
        .update({ credits_topup: newTopup })
        .eq('id', userId);

      if (updateError) throw updateError;

      const { data: { user } } = await supabase.auth.getUser();

      const { data: adminUser } = await supabase
        .from('users')
        .select('id')
        .eq('auth_user_id', user?.id)
        .maybeSingle();

      const { error: logError } = await supabase
        .from('credit_allocations')
        .insert({
          user_id: userId,
          allocation_amount: amount,
          allocation_type: 'admin_adjustment',
          credit_type: 'topup',
          notes: topupNote || (amount > 0 ? 'Handmatig toegevoegd door admin' : 'Handmatig afgetrokken door admin'),
          created_by: adminUser?.id || null
        });

      if (logError) throw logError;

      setMessage(amount > 0 ? 'Topup credits toegevoegd!' : 'Credits afgetrokken!');
      setShowAddTopup(false);
      setTopupAmount('');
      setTopupNote('');
      await loadCreditData();
    } catch (error) {
      console.error('Error adding topup:', error);
      setMessage('Fout bij toevoegen topup credits');
    } finally {
      setSaving(false);
    }
  };

  const handleExportCSV = () => {
    const filtered = filterHistory(combinedHistory);
    const csvContent = [
      ['Datum', 'Type', 'Credits', 'Credit Type', 'Notities'].join(','),
      ...filtered.map(entry => {
        if (entry.type === 'allocation') {
          const alloc = entry.data as CreditAllocation;
          return [
            new Date(alloc.created_at).toLocaleString('nl-NL'),
            alloc.allocation_type,
            alloc.allocation_amount > 0 ? `+${alloc.allocation_amount}` : alloc.allocation_amount,
            alloc.credit_type,
            alloc.notes || '-'
          ].join(',');
        } else {
          const log = entry.data as CreditLogEntry;
          return [
            new Date(log.created_at).toLocaleString('nl-NL'),
            log.action_type,
            `-${log.credits_used}`,
            'usage',
            log.ai_provider || '-'
          ].join(',');
        }
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `credit-log-${userId}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  if (loading) {
    return <div className="text-center py-8">Laden...</div>;
  }

  if (!creditData) {
    return <div className="text-center py-8 text-red-600">Credit gegevens niet gevonden</div>;
  }

  const totalCredits = creditData.credits_regular + creditData.credits_topup;
  const isLow = totalCredits < 500;
  const isNegative = totalCredits < 0;

  type CombinedEntry = {
    id: string;
    created_at: string;
    type: 'allocation' | 'usage';
    data: CreditAllocation | CreditLogEntry;
  };

  const combinedHistory: CombinedEntry[] = [
    ...allocations.map(alloc => ({
      id: alloc.id,
      created_at: alloc.created_at,
      type: 'allocation' as const,
      data: alloc
    })),
    ...creditLogs.map(log => ({
      id: log.id,
      created_at: log.created_at,
      type: 'usage' as const,
      data: log
    }))
  ].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const filterHistory = (history: CombinedEntry[]) => {
    let filtered = [...history];

    // Timeframe filter
    if (timeframeFilter !== 'all') {
      const now = new Date();
      const cutoffDate = new Date();

      switch (timeframeFilter) {
        case 'day':
          cutoffDate.setDate(now.getDate() - 1);
          break;
        case 'week':
          cutoffDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          cutoffDate.setMonth(now.getMonth() - 1);
          break;
        case 'quarter':
          cutoffDate.setMonth(now.getMonth() - 3);
          break;
        case 'year':
          cutoffDate.setFullYear(now.getFullYear() - 1);
          break;
      }

      filtered = filtered.filter(entry => new Date(entry.created_at) >= cutoffDate);
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(entry => {
        if (typeFilter === 'allocation') return entry.type === 'allocation';
        if (typeFilter === 'usage') return entry.type === 'usage';
        if (typeFilter === 'topup') {
          return entry.type === 'allocation' && (entry.data as CreditAllocation).credit_type === 'topup';
        }
        if (typeFilter === 'regular') {
          return entry.type === 'allocation' && (entry.data as CreditAllocation).credit_type === 'regular';
        }
        return true;
      });
    }

    return filtered;
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Reguliere Credits</span>
            <Calendar className="w-4 h-4 text-gray-400" />
          </div>
          <div className="text-3xl font-bold text-gray-900">{creditData.credits_regular.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">Reset op {new Date(creditData.next_billing_date).toLocaleDateString('nl-NL')}</div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Topup Credits</span>
            <TrendingUp className="w-4 h-4 text-gray-400" />
          </div>
          <div className="text-3xl font-bold text-blue-600">{creditData.credits_topup.toLocaleString()}</div>
          <div className="text-xs text-gray-500 mt-1">Blijven staan</div>
        </div>

        <div className={`bg-white border rounded-lg p-6 ${isNegative ? 'border-red-300 bg-red-50' : isLow ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">Totaal Beschikbaar</span>
            <Coins className={`w-4 h-4 ${isNegative ? 'text-red-500' : isLow ? 'text-yellow-500' : 'text-gray-400'}`} />
          </div>
          <div className={`text-3xl font-bold ${isNegative ? 'text-red-600' : isLow ? 'text-yellow-600' : 'text-gray-900'}`}>
            {totalCredits.toLocaleString()}
          </div>
          {isNegative && (
            <div className="flex items-center gap-1 text-xs text-red-600 mt-1">
              <AlertCircle className="w-3 h-3" />
              Negatief saldo
            </div>
          )}
          {isLow && !isNegative && (
            <div className="flex items-center gap-1 text-xs text-yellow-600 mt-1">
              <AlertCircle className="w-3 h-3" />
              Laag saldo
            </div>
          )}
        </div>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Maandelijks budget:</span>
            <span className="ml-2 font-medium">{creditData.credits_allocated_monthly} credits</span>
          </div>
          <div>
            <span className="text-gray-600">Negatief limiet:</span>
            <span className="ml-2 font-medium">{creditData.credits_negative_limit} credits</span>
          </div>
          <div>
            <span className="text-gray-600">Billing start:</span>
            <span className="ml-2 font-medium">{new Date(creditData.billing_cycle_start_date).toLocaleDateString('nl-NL')}</span>
          </div>
          <div>
            <span className="text-gray-600">Laatste refill:</span>
            <span className="ml-2 font-medium">{new Date(creditData.credits_last_refilled_at).toLocaleDateString('nl-NL')}</span>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Topup Toevoegen</h3>
        <button
          onClick={() => setShowAddTopup(!showAddTopup)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Topup Credits
        </button>
      </div>

      {showAddTopup && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Aantal Credits</label>
              <input
                type="number"
                value={topupAmount}
                onChange={(e) => setTopupAmount(e.target.value)}
                placeholder="2500 (positief om toe te voegen, negatief om af te trekken)"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Gebruik positieve getallen om credits toe te voegen, negatieve getallen om af te trekken
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Notitie (optioneel)</label>
              <input
                type="text"
                value={topupNote}
                onChange={(e) => setTopupNote(e.target.value)}
                placeholder="Reden voor topup..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            {message && (
              <div className={`p-3 rounded-lg ${message.includes('Fout') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                {message}
              </div>
            )}
            <div className="flex gap-3">
              <button
                onClick={handleAddTopup}
                disabled={saving}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {saving ? 'Bezig...' : 'Credits Bijwerken'}
              </button>
              <button
                onClick={() => {
                  setShowAddTopup(false);
                  setMessage('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <History className="w-5 h-5" />
            Credit Geschiedenis
          </h3>
          {combinedHistory.length > 0 && (
            <button
              onClick={handleExportCSV}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          )}
        </div>

        {combinedHistory.length === 0 ? (
          <div className="text-center py-8 text-gray-500">Geen credit geschiedenis</div>
        ) : (
          <>
            {/* Filters */}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap">
                {/* Timeframe Filter */}
                <div className="relative">
                  <select
                    value={timeframeFilter}
                    onChange={(e) => setTimeframeFilter(e.target.value)}
                    className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="day">Laatste 24 uur</option>
                    <option value="week">Laatste week</option>
                    <option value="month">Laatste maand</option>
                    <option value="quarter">Laatste kwartaal</option>
                    <option value="year">Laatste jaar</option>
                    <option value="all">Alle tijd</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                </div>

                {/* Type Filter */}
                <div className="relative">
                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">Alle Types</option>
                    <option value="allocation">Allocaties</option>
                    <option value="usage">Verbruik</option>
                    <option value="topup">Topup Credits</option>
                    <option value="regular">Reguliere Credits</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                </div>

                {/* Reset Filters Button */}
                {(timeframeFilter !== 'month' || typeFilter !== 'all') && (
                  <button
                    onClick={() => {
                      setTimeframeFilter('month');
                      setTypeFilter('all');
                    }}
                    className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 font-medium"
                  >
                    <XCircle className="w-4 h-4" />
                    Reset Filters
                  </button>
                )}
              </div>

              {/* Results Count */}
              <div className="text-sm text-gray-600 flex items-center gap-2">
                <Filter className="w-4 h-4" />
                <span>
                  {(() => {
                    const filtered = filterHistory(combinedHistory);
                    return `${filtered.length} van ${combinedHistory.length} logs`;
                  })()}
                </span>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Datum</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Credits</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Credit Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Notities</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filterHistory(combinedHistory).map((entry) => {
                  if (entry.type === 'allocation') {
                    const alloc = entry.data as CreditAllocation;
                    return (
                      <tr key={entry.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {new Date(alloc.created_at).toLocaleString('nl-NL')}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                            alloc.allocation_type === 'monthly_refill' ? 'bg-blue-100 text-blue-700' :
                            alloc.allocation_type === 'topup_purchase' ? 'bg-green-100 text-green-700' :
                            alloc.allocation_type === 'admin_adjustment' ? 'bg-purple-100 text-purple-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {alloc.allocation_type}
                          </span>
                        </td>
                        <td className={`px-4 py-3 text-sm font-medium ${
                          alloc.allocation_amount > 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {alloc.allocation_amount > 0 ? '+' : ''}{alloc.allocation_amount.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                            alloc.credit_type === 'regular' ? 'bg-gray-100 text-gray-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                            {alloc.credit_type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{alloc.notes || '-'}</td>
                      </tr>
                    );
                  } else {
                    const log = entry.data as CreditLogEntry;
                    return (
                      <tr key={entry.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {new Date(log.created_at).toLocaleString('nl-NL')}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                            log.success ? 'bg-gray-100 text-gray-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {log.action_type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-red-600">
                          -{log.credits_used.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className="inline-flex px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
                            usage
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {log.ai_provider || '-'}
                          {!log.success && ' (gefaald)'}
                        </td>
                      </tr>
                    );
                  }
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {purchases.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Topup Aankopen</h3>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Datum</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Pakket</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Credits</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Prijs</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {purchases.map((purch) => (
                  <tr key={purch.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {new Date(purch.created_at).toLocaleString('nl-NL')}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{purch.package_type}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">{purch.credits_amount.toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm text-gray-900">€{purch.price_paid.toFixed(2)}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                        purch.payment_status === 'completed' ? 'bg-green-100 text-green-700' :
                        purch.payment_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        purch.payment_status === 'failed' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {purch.payment_status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
